import { Addresses } from '@/domain/addresses';
import { getChainConstant } from '@/domain/chain';
import useChainId from '@/hooks/useChainId';
import FloorV1TokenMerkleCLaimAbi from '@/infrastructure/abis/FloorV1TokenMerkleClaim';
import { Address } from 'viem';
import { useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { generateProof } from './utils';
import useLeaf from './useLeaf';
import { useCallback } from 'react';

const useFloorV1TokenMerkleClaim = ({
  address,
  amount,
}: {
  address?: Address;
  amount?: bigint;
}) => {
  const chainId = useChainId();
  const { writeContract, data: hash, isPending, error } = useWriteContract();

  const { data: leaf } = useLeaf({
    recipient: address,
    amount: amount,
  });

  const migrate = useCallback(() => {
    if (leaf && address && amount) {
      const proof = generateProof(leaf);

      writeContract({
        abi: FloorV1TokenMerkleCLaimAbi,
        address: getChainConstant(
          Addresses,
          chainId,
          'FloorV1TokenMerkleClaim',
        ),
        functionName: 'verify',
        args: [proof, address, amount],
      });
    }
  }, [leaf, address, amount, chainId, writeContract]);

  const {
    isLoading: isConfirming,
    isSuccess: isConfirmed,
    data,
  } = useWaitForTransactionReceipt({
    hash: hash,
  });

  return {
    migrate,
    isPending,
    isConfirming,
    isConfirmed,
    error,
    receipt: data,
  };
};

export default useFloorV1TokenMerkleClaim;
